import classNames from 'classnames';
import type { CSSProperties } from 'react';
import { useAnchorable } from '../../hooks/useAnchorable';
import { appendProtocol } from '../../utils/link';
import {
  articleContent,
  articleWrapper,
  byline,
  categoriesContainer,
  category,
  cta,
  ctaContainer,
  heading,
  paragraph,
  pubDate,
  rssContainer,
} from './Rss.css';
import type { TPostRssProps } from './Rss.types';
import { Thumbnail } from './components/Thumbnail';

const rssThumbnailPositions = {
  LEFT: 'Left of content',
  RIGHT: 'Right of content',
  TOP: 'Above Title',
  BOTTOM: 'Below Title',
  ALTERNATING_HORIZONTAL: 'Alternating Horizontally',
};

function getNumberOfColumns(entryLayout: string | null | undefined): number {
  if (!entryLayout) return 1;

  const columnCount = Number.parseInt(entryLayout.replace('-col', ''));
  return Number.isNaN(columnCount) ? 1 : columnCount;
}

function extractFirstImage(htmlString: string) {
  if (!htmlString) return null;

  const match = htmlString.match(/<img[^>]+src="([^">]+)"/);
  return match ? match[1] : null;
}

const getThumbnailSrc = (article: any) => {
  if (article.thumbnail) {
    return article.thumbnail;
  }
  return extractFirstImage(article.content) || '';
};

const thumbnailIsHorizontal = (thumbnailPosition: string) => {
  return [
    rssThumbnailPositions.LEFT,
    rssThumbnailPositions.RIGHT,
    rssThumbnailPositions.ALTERNATING_HORIZONTAL,
  ].includes(thumbnailPosition);
};

export const PostRss = (props: TPostRssProps) => {
  const { element, rewriteImageSrc } = props;
  const { attrs } = element;

  const { anchorAttributes } = useAnchorable(attrs);

  const { data, settings } = attrs;

  const thumbnailAsLink =
    settings?.ctaStyle === 'Thumbnail as link' ||
    settings?.ctaStyle === 'Title and Thumbnail as link' ||
    settings?.ctaStyle === 'Title, Thumbnail and Button as CTA' ||
    settings?.ctaStyle === 'Title, Thumbnail and Link as CTA';

  const titleAsLink =
    settings?.ctaStyle === 'Title as link' ||
    settings?.ctaStyle === 'Title and Thumbnail as link' ||
    settings?.ctaStyle === 'Title, Thumbnail and Button as CTA' ||
    settings?.ctaStyle === 'Title, Thumbnail and Link as CTA';

  const showTitleAboveThumbnail = settings?.thumbnailPosition === rssThumbnailPositions.BOTTOM;

  const ctaButton = settings?.ctaStyle === 'CTA button' || settings?.ctaStyle === 'Title, Thumbnail and Button as CTA';
  const ctaLink = settings?.ctaStyle === 'CTA link' || settings?.ctaStyle === 'Title, Thumbnail and Link as CTA';

  const articleWrapperClassNames = (index: number): string => {
    if (settings?.thumbnailPosition === rssThumbnailPositions.ALTERNATING_HORIZONTAL) {
      return index % 2 === 0 ? 'flex-col sm:flex-row' : 'flex-col sm:flex-row-reverse';
    }
    if (settings?.thumbnailPosition === rssThumbnailPositions.LEFT) {
      return 'flex-col sm:flex-row';
    }
    if (settings?.thumbnailPosition === rssThumbnailPositions.RIGHT) {
      return 'flex-col sm:flex-row-reverse';
    }

    return 'flex-col';
  };

  return (
    <div
      {...anchorAttributes}
      className={rssContainer}
      style={
        {
          '--number-of-columns': getNumberOfColumns(attrs.entryLayout),
          '--cta-alignment': settings?.ctaAlignment?.toLowerCase() || 'left',
        } as CSSProperties
      }
    >
      {data?.content?.slice(0, settings?.entriesToShow || 1)?.map((article, index) => {
        const thumbnailSrc = getThumbnailSrc(article);
        const articleThumbnailSrc = thumbnailSrc && rewriteImageSrc ? rewriteImageSrc(thumbnailSrc) : thumbnailSrc;
        const articleLink = article.link ? appendProtocol(article.link) : article.link;

        return (
          <div className={classNames(articleWrapper, articleWrapperClassNames(index))} key={article.link}>
            {settings?.displayThumbnail &&
            articleThumbnailSrc &&
            thumbnailIsHorizontal(settings?.thumbnailPosition || '') ? (
              <Thumbnail
                asLink={thumbnailAsLink}
                articleLink={articleLink}
                articleTitle={article.title}
                articleThumbnailSrc={articleThumbnailSrc}
              />
            ) : null}
            <div className={articleContent}>
              {settings?.displayTitle &&
              (showTitleAboveThumbnail || thumbnailIsHorizontal(settings?.thumbnailPosition || '')) ? (
                titleAsLink && !!article.link ? (
                  <a href={articleLink} target="_blank" rel="noreferrer" data-skip-tracking="true">
                    <h2 className={heading}>{article.title}</h2>
                  </a>
                ) : (
                  <h2 className={heading}>{article.title}</h2>
                )
              ) : null}
              {settings?.displayThumbnail &&
              articleThumbnailSrc &&
              !thumbnailIsHorizontal(settings?.thumbnailPosition || '') ? (
                <Thumbnail
                  asFullWidth
                  asLink={thumbnailAsLink}
                  articleLink={articleLink}
                  articleTitle={article.title}
                  articleThumbnailSrc={articleThumbnailSrc}
                />
              ) : null}

              {settings?.displayCategories && article.categories && article.categories.length > 0 && (
                <div className={categoriesContainer}>
                  {article.categories.map((c) => (
                    <span key={c} className={category}>
                      {c}
                    </span>
                  ))}
                </div>
              )}

              {settings?.displayTitle &&
              !showTitleAboveThumbnail &&
              !thumbnailIsHorizontal(settings?.thumbnailPosition || '') ? (
                titleAsLink && !!article.link ? (
                  <a href={articleLink} target="_blank" rel="noreferrer" data-skip-tracking="true">
                    <h2 className={heading}>{article.title}</h2>
                  </a>
                ) : (
                  <h2 className={heading}>{article.title}</h2>
                )
              ) : null}
              {settings?.displayAuthor && article.author && <p className={byline}>by {article.author}</p>}
              {settings?.displayPubDate && article.pub_date && (
                <p className={pubDate}>
                  {new Date(article.pub_date).toLocaleDateString('en-US', {
                    month: 'long',
                    day: 'numeric',
                    year: 'numeric',
                  })}
                </p>
              )}
              {settings?.displayDescription && article.description && (
                <p className={paragraph}>{article.description}</p>
              )}
              {settings?.displayContent && article.content && (
                <p className={paragraph} dangerouslySetInnerHTML={{ __html: article.content }} />
              )}
              {settings?.displayCta && (ctaButton || ctaLink) && article.link && (
                <div className={ctaContainer}>
                  <a href={articleLink} target="_blank" rel="noreferrer" data-skip-tracking="true">
                    {ctaButton ? (
                      <button type="button" className={cta}>
                        {settings?.ctaText || ''}
                      </button>
                    ) : (
                      settings?.ctaText || ''
                    )}
                  </a>
                </div>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
};
